import {
  ActionValidation,
  BooleanValidation,
  ConfigOptionValidation,
  StringValidation,
} from '../../../utils/component-validations';
import { and, equal } from 'macro-decorators';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Button from '../core/button.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import shiftFocusToParent from '../../../modifiers/shift-focus-to-parent';
import styles from './tag.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const BASIC = 'basic';
const FILTER = 'filter';
const INTERACTIVE = 'interactive';
const ON_CLICK = 'onClick';
const ON_CLOSE = 'onClose';
const TYPES = [BASIC, FILTER, INTERACTIVE];

export default class SpdsIndicatorTag extends Super {
  baseArguments = [
    new BooleanValidation({ optional: ['disabled', 'small'] }),
    new ConfigOptionValidation({
      required: [
        {
          name: 'type',
          options: TYPES,
        },
      ],
    }),
    new StringValidation({ optional: ['icon'], required: ['label'] }),
  ];
  baseClass = 'spds-indicator-tag';
  classesToMapToArgs = ['disabled', 'small'];
  classesToMapToAttrs = ['filter', 'interactive'];
  componentName = 'Spds::Indicator::Tag';
  elementsToRegister = [{ name: 'clearButton', selector: 'button' }];
  styles = styles;

  @equal('args.type', FILTER) filter;
  @equal('args.type', INTERACTIVE) interactive;
  @and('filter', 'args.onClose') showClose;

  get arguments() {
    return [...this.baseArguments, this[`${this.args.type}VariantArguments`]];
  }

  get basicVariantArguments() {
    return this.createVariantArgs([], [ON_CLICK, ON_CLOSE], BASIC);
  }

  get filterVariantArguments() {
    return this.createVariantArgs([ON_CLOSE], [ON_CLICK], FILTER);
  }

  get interactiveVariantArguments() {
    return this.createVariantArgs([ON_CLICK], [ON_CLOSE], INTERACTIVE);
  }

  createVariantArgs(required, forbidden, variant) {
    return new ActionValidation({
      forbidden: forbidden.map(name => {
        return { name, reason: `'${variant}' variant does not support @${name}` };
      }),
      required,
    });
  }

  <template>
    {{#if this.interactive}}
      <Button
        aria-label={{@label}}
        class={{this.classes}}
        data-test-spds-indicator-tag={{@type}}
        id={{@id}}
        type="button"
        {{on "click" @onClick}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @icon}}
          <FlexRow @tag="span" class="spds-tag-icon" data-test-spds-indicator-tag-icon>
            {{svgJar @icon}}
          </FlexRow>
        {{/if}}
        <FlexRow @tag="span" class="spds-tag-label" data-test-spds-indicator-tag-label>
          {{@label}}
        </FlexRow>
      </Button>
    {{else}}
      <FlexRow
        @discardClick={{true}}
        @fitContent={{true}}
        @inline={{true}}
        @tag="span"
        aria-label={{@label}}
        class={{this.classes}}
        data-test-spds-indicator-tag={{@type}}
        {{this.setupComponent}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @icon}}
          <FlexRow @tag="span" class="spds-tag-icon" data-test-spds-indicator-tag-icon>
            {{svgJar @icon}}
          </FlexRow>
        {{/if}}
        <FlexRow @tag="span" class="spds-tag-label" data-test-spds-indicator-tag-label>
          {{@label}}
        </FlexRow>
        {{#if this.showClose}}
          <Button
            aria-label="Remove {{@label}} filter"
            class="spds-filter-tag-close-button"
            data-test-spds-indicator-tag-close
            disabled={{@disabled}}
            type="button"
            {{on "click" (fn @onClose @label)}}
            {{shiftFocusToParent}}
          >
            {{svgJar "x-mark"}}
          </Button>
        {{/if}}
      </FlexRow>
    {{/if}}
  </template>
}
